import { IconButton } from '@ardoq/button';
import { colors } from '@ardoq/design-tokens';
import { IconName } from '@ardoq/icons';
import _ from 'lodash';
import { FC, useState } from 'react';
import styled from 'styled-components';
import { CollapsibleIsland } from './CollapsibleIsland';
import { PreviewMatrix, PreviewMatrixProps } from './PreviewMatrix';
import { Island, ISLAND_SELECTOR } from '@ardoq/page-layout';
import { FlexBox, Box, Stack } from '@ardoq/layout';
import { Header1, Header3, InlineText, Paragraph } from '@ardoq/typography';
import { type I18nLocale, t } from '@ardoq/i18n';

// Types

export type ImportPreviewLabels = {
  detailsTitle?: string;
  assetsHeader?: string;
  workspacesLabel?: string;
  collapseLabel?: string;
  expandLabel?: string;
  seeMoreLabel?: string;
  toCreateLabel?: string;
  toUpdateLabel?: string;
  toDeleteLabel?: string;
};

type ImportPreviewProps = PreviewMatrixProps & {
  workspaces: WorkspaceProps[];
  labels?: ImportPreviewLabels;
};

type WorkspaceProps = {
  name: string;
  tableTypes: TableTypeProps[];
};

type TableTypeProps = {
  name: string;
  tableChanges: TableChangesProps[];
};

type TableChangesProps = {
  name: string;
  changes: { updated: number; deleted: number; created: number };
};

// Main Component

export const ImportPreview: FC<ImportPreviewProps> = ({
  i18nLocale,
  workspaces,
  totals,
  labels = {},
}) => {
  const {
    detailsTitle = t(
      i18nLocale,
      'integrations:importPreview.detailsTitle',
      'Details of your import'
    ),
    assetsHeader = t(
      i18nLocale,
      'integrations:importPreview.assetsHeader',
      'These assets will be created or updated during import'
    ),
    workspacesLabel = t(
      i18nLocale,
      'integrations:importPreview.workspacesLabel',
      'Workspaces'
    ),
    collapseLabel = t(
      i18nLocale,
      'integrations:collapsibleIsland.collapse',
      'Collapse'
    ),
    expandLabel = t(
      i18nLocale,
      'integrations:collapsibleIsland.expand',
      'Expand'
    ),
    seeMoreLabel = t(
      i18nLocale,
      'integrations:importPreview.seeMoreLabel',
      'see more...'
    ),
    toCreateLabel,
    toUpdateLabel,
    toDeleteLabel,
  } = labels;
  const [visualState, setVisualState] = useState<VisualState>(
    initialVisualState(workspaces)
  );

  const workspaceExpansionToggler = (workspaceName: string) => {
    return () => {
      setVisualState(
        _.update({ ...visualState }, [workspaceName, 'isExpanded'], x => !x)
      );
    };
  };

  const isAtLeastPartiallyExpanded = () => {
    return _.values(visualState).some(v => v.isExpanded === true);
  };

  const toggleExpand = () => {
    const v = !isAtLeastPartiallyExpanded();

    setVisualState(
      _.reduce(
        workspaces,
        (m: VisualState, w) => _.set(m, [w.name, 'isExpanded'], v),
        { ...visualState }
      )
    );
  };

  const expandEllipsis = (workspaceName: string, TableTypeName: string) => {
    setVisualState(
      _.set(
        { ...visualState },
        [workspaceName, 'ellipsis', TableTypeName],
        false
      )
    );
  };

  return (
    <Island>
      <Stack gap="medium">
        <FlexBox paddingY="small">
          <Header1>{detailsTitle}</Header1>
        </FlexBox>
        <FlexBox padding="medium" justify="center">
          <PreviewMatrix
            i18nLocale={i18nLocale}
            totals={totals}
            transferDirection={'import'}
            toCreateLabel={toCreateLabel}
            toUpdateLabel={toUpdateLabel}
            toDeleteLabel={toDeleteLabel}
          />
        </FlexBox>
        <FlexBox paddingY="small">
          <Header3>{assetsHeader}</Header3>
        </FlexBox>
        <ImportPreviewWorkspacesSummary>
          <FlexBox align="center" marginBottom="medium">
            <Header3>
              {workspaces.length} {workspacesLabel}
            </Header3>
            <IconButton
              label={isAtLeastPartiallyExpanded() ? collapseLabel : expandLabel}
              iconName={
                isAtLeastPartiallyExpanded()
                  ? IconName.CHEVRON_UP
                  : IconName.CHEVRON_DOWN
              }
              onClick={toggleExpand}
            />
          </FlexBox>
          <Stack gap="medium">
            {workspaces.map((workspace, idx) => (
              <CollapsibleIsland
                i18nLocale={i18nLocale}
                key={idx}
                title={workspace.name}
                onToggleExpand={workspaceExpansionToggler(workspace.name)}
                isExpanded={visualState[workspace.name]?.isExpanded ?? false}
                collapseLabel={collapseLabel}
                expandLabel={expandLabel}
              >
                <Stack gap="medium">
                  {workspace.tableTypes.map(tableType => (
                    <TableTypeSummary
                      key={tableType.name}
                      i18nLocale={i18nLocale}
                      TableType={tableType}
                      isEllipted={
                        visualState[workspace.name]?.ellipsis[tableType.name]
                      }
                      onSeeMore={() =>
                        expandEllipsis(workspace.name, tableType.name)
                      }
                      seeMoreLabel={seeMoreLabel}
                    />
                  ))}
                </Stack>
              </CollapsibleIsland>
            ))}
          </Stack>
        </ImportPreviewWorkspacesSummary>
      </Stack>
    </Island>
  );
};

// Visual State

type WorkspaceName = string;

type TableTypeName = string;

type VisualState = Record<
  WorkspaceName,
  { isExpanded: boolean; ellipsis: Record<TableTypeName, boolean> }
>;

const initialVisualState = (workspaces: WorkspaceProps[]) => {
  return _.reduce(
    workspaces,
    (m: VisualState, w, idx) =>
      _.set(m, [w.name], {
        isExpanded: idx === 0, // by default the first workspace only is expanded
        ellipsis: _.reduce(
          w.tableTypes,
          (m: Record<string, boolean>, c) => _.set(m, [c.name], true),
          {}
        ),
      }),
    {}
  );
};

// subcomponents

type TableChangeProps = {
  type: string;
  name: string;
  count: number;
};

const tableChangeCount = ({ changes }: TableChangesProps) => {
  return changes.updated + changes.created + changes.deleted;
};

const sortedTableChanges = ({
  tableChanges,
}: TableTypeProps): TableChangeProps[] => {
  const flattenChanges = tableChanges.flatMap(({ name, changes }) =>
    Object.entries(changes).map(([op, cnt]) => {
      return { type: op, name: name, count: cnt };
    })
  );
  const withoutEmpties = _.filter(flattenChanges, x => x.count > 0);
  return _.sortBy(withoutEmpties, ['type', 'name']);
};

const ELLIPSIS_TRESHOLD = 7;

const TableTypeSummary = ({
  i18nLocale,
  TableType,
  isEllipted,
  onSeeMore,
  seeMoreLabel,
}: {
  i18nLocale: I18nLocale;
  TableType: TableTypeProps;
  isEllipted: boolean;
  onSeeMore: () => void;
  seeMoreLabel: string;
}) => {
  const renderTableTypeChanges = (changes: TableChangeProps[]) => {
    return changes.map(({ type, name, count }) => (
      <Paragraph variant="text2" key={[type, name, count].toString()}>
        {type} {name} ({count})
      </Paragraph>
    ));
  };

  const changes = sortedTableChanges(TableType);

  if (changes.length === 0) {
    return <></>;
  }

  const tableCount = _.uniq(_.map([...changes], x => x.name)).length;
  const itemCount = TableType.tableChanges.reduce(
    (acc, tableChange) => acc + tableChangeCount(tableChange),
    0
  );
  const elliptable = isEllipted && changes.length > ELLIPSIS_TRESHOLD;
  const visibleChanges = elliptable
    ? _.take(changes, ELLIPSIS_TRESHOLD)
    : changes;

  return (
    <Box key={TableType.name}>
      <Header3>
        {t(
          i18nLocale,
          'integrations:importPreview.typeSummary',
          { tableCount, typeName: TableType.name, itemCount },
          '{{tableCount}} {{typeName}} type containing {{itemCount}} unique items'
        )}
      </Header3>
      <FlexBox paddingY="small" paddingX="medium">
        <Stack gap="small">
          {renderTableTypeChanges(visibleChanges)}
          {elliptable && (
            <div onClick={onSeeMore}>
              <InlineText variant="text2" color="textAction">
                {seeMoreLabel}
              </InlineText>
            </div>
          )}
        </Stack>
      </FlexBox>
    </Box>
  );
};

// styles

const ImportPreviewWorkspacesSummary = styled.div`
  ${ISLAND_SELECTOR} {
    min-width: 300px;
    background-color: ${colors.surfaceDefaultSubtle};
  }
`;
